import { useCallback, useRef, useEffect } from 'react';
import { GridApi, GridReadyEvent, BodyScrollEvent } from 'ag-grid-community';

interface UseAgGridInfiniteScrollProps {
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  threshold?: number;
  onLoadMore?: () => void;
}

/**
 * Obtiene el viewport de scroll de AG Grid.
 * AG Grid puede usar diferentes selectores según versión/configuración.
 */
const getScrollViewport = (): HTMLElement | null => {
  return (
    document.querySelector('.ag-body-viewport') ??
    document.querySelector('.ag-body-horizontal-scroll-viewport') ??
    document.querySelector('[ref="eBodyViewport"]')
  ) as HTMLElement | null;
};

const checkShouldFetch = (
  viewport: HTMLElement,
  threshold: number
): boolean => {
  const scrollTop = viewport.scrollTop;
  const scrollHeight = viewport.scrollHeight;
  const clientHeight = viewport.clientHeight;
  const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
  return distanceFromBottom <= threshold;
};

export const useAgGridInfiniteScroll = ({
  fetchNextPage,
  hasNextPage = false,
  isFetchingNextPage = false,
  threshold = 500,
  onLoadMore,
}: UseAgGridInfiniteScrollProps) => {
  const gridApiRef = useRef<GridApi | null>(null);
  const isLoadingRef = useRef(false);
  const fetchNextPageRef = useRef(fetchNextPage);
  const hasNextPageRef = useRef(hasNextPage);
  const isFetchingNextPageRef = useRef(isFetchingNextPage);

  // Mantener refs actualizados para evitar stale closures
  fetchNextPageRef.current = fetchNextPage;
  hasNextPageRef.current = hasNextPage;
  isFetchingNextPageRef.current = isFetchingNextPage;

  const handleGridReady = useCallback((event: GridReadyEvent) => {
    gridApiRef.current = event.api;
  }, []);

  const tryFetchNextPage = useCallback(() => {
    if (
      !hasNextPageRef.current ||
      isFetchingNextPageRef.current ||
      isLoadingRef.current ||
      !fetchNextPageRef.current
    ) {
      return;
    }
    isLoadingRef.current = true;
    fetchNextPageRef.current?.();
    onLoadMore?.();
  }, [onLoadMore]);

  const handleBodyScroll = useCallback(
    (_event: BodyScrollEvent) => {
      const viewport = getScrollViewport();
      if (!viewport) return;

      if (checkShouldFetch(viewport, threshold)) {
        tryFetchNextPage();
      }
    },
    [threshold, tryFetchNextPage]
  );

  /**
   * Verifica si el usuario está cerca del final y carga más si aplica.
   * Útil como fallback cuando onBodyScroll no se dispara (ej: al cambiar datos,
   * redimensionar, o cuando AG Grid no emite el evento correctamente).
   */
  const checkAndFetch = useCallback(() => {
    const viewport = getScrollViewport();
    if (!viewport) return;
    if (checkShouldFetch(viewport, threshold)) {
      tryFetchNextPage();
    }
  }, [threshold, tryFetchNextPage]);

  useEffect(() => {
    if (!isFetchingNextPage) {
      isLoadingRef.current = false;
    }
  }, [isFetchingNextPage]);

  useEffect(() => {
    isLoadingRef.current = false;
  }, [fetchNextPage, hasNextPage]);

  const gridOptions = {
    onGridReady: handleGridReady,
    onBodyScroll: handleBodyScroll,
  };

  return {
    gridOptions,
    gridApi: gridApiRef.current,
    checkAndFetch,
    fetchNextPage: tryFetchNextPage,
  };
};
